import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { Link } from "react-router-dom";
import {
  MdSms,
  MdSend,
  MdCheckCircle,
  MdError,
  MdCampaign,
  MdAccountBalanceWallet,
  MdTrendingUp,
  MdArrowForward,
} from "react-icons/md";

// TODO:
// import {
//   getBulkSMSDashboard,
//   getTodayBulkSMSDashboard,
// } from "../../../redux/services/bulkSMSDashboardServices/bulkSMSDashboard.Services";

const BulkSMSDashboard = () => {
  const [view, setView] = useState("overall");
  const [loading, setLoading] = useState(true);

  const [summary, setSummary] = useState({});
  const [todaySummary, setTodaySummary] = useState({});

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        setLoading(true);

        // const [overall, today] = await Promise.all([
        //   getBulkSMSDashboard(),
        //   getTodayBulkSMSDashboard(),
        // ]);

        // setSummary(overall?.data || {});
        // setTodaySummary(today?.data || {});

        setSummary({});
        setTodaySummary({});
      } catch (error) {
        console.error("Bulk SMS Dashboard Error:", error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  const overallTiles = useMemo(
    () => [
      {
        icon: <MdAccountBalanceWallet />,
        label: "SMS Balance",
        href: "/sms-balance",
        points: summary?.sms_balance || 0,
        accent: "cyan",
        helper: "Available credits",
      },
      {
        icon: <MdSend />,
        label: "SMS Sent",
        href: "/sms-history",
        points: summary?.sms_sent || 0,
        accent: "blue",
        helper: "Total messages",
      },
      {
        icon: <MdCheckCircle />,
        label: "Delivered",
        href: "/sms-history",
        points: summary?.delivered || 0,
        accent: "green",
        helper: "Delivered messages",
      },
      {
        icon: <MdError />,
        label: "Failed",
        href: "/sms-history",
        points: summary?.failed || 0,
        accent: "red",
        helper: "Failed messages",
      },
      {
        icon: <MdCampaign />,
        label: "Campaigns",
        href: "/sms-campaign-list",
        points: summary?.campaigns || 0,
        accent: "purple",
        helper: "SMS campaigns",
      },
      {
        icon: <MdTrendingUp />,
        label: "Delivery Rate",
        href: "/sms-report",
        points: `${summary?.delivery_rate || 0}%`,
        accent: "orange",
        helper: "Overall delivery",
      },
    ],
    [summary]
  );

  const todayTiles = useMemo(
    () => [
      {
        icon: <MdSend />,
        label: "Sent Today",
        href: "/sms-history",
        points: todaySummary?.sms_sent || 0,
        accent: "cyan",
        helper: "Today's messages",
      },
      {
        icon: <MdCheckCircle />,
        label: "Delivered Today",
        href: "/sms-history",
        points: todaySummary?.delivered || 0,
        accent: "green",
        helper: "Successful delivery",
      },
      {
        icon: <MdError />,
        label: "Failed Today",
        href: "/sms-history",
        points: todaySummary?.failed || 0,
        accent: "red",
        helper: "Failed messages",
      },
      {
        icon: <MdCampaign />,
        label: "Campaigns Today",
        href: "/sms-campaign-list",
        points: todaySummary?.campaigns || 0,
        accent: "purple",
        helper: "Today's campaigns",
      },
      {
        icon: <MdTrendingUp />,
        label: "Delivery Rate",
        href: "/sms-report",
        points: `${todaySummary?.delivery_rate || 0}%`,
        accent: "orange",
        helper: "Today's delivery",
      },
    ],
    [todaySummary]
  );

  const currentData = view === "overall" ? summary : todaySummary;
  const tiles = view === "overall" ? overallTiles : todayTiles;

  const deliveryChart = {
    series: [
      Number(currentData?.delivered || 0),
      Number(currentData?.failed || 0),
      Number(currentData?.pending || 0),
    ],
    options: {
      chart: {
        type: "donut",
        toolbar: { show: false },
      },
      labels: ["Delivered", "Failed", "Pending"],
      legend: {
        position: "bottom",
      },
    },
  };

  const campaignPerformance = currentData?.campaign_performance || [];

  const campaignChart = {
    series: [
      {
        name: "Sent",
        data: campaignPerformance.map((item) =>
          Number(item?.sent || 0)
        ),
      },
      {
        name: "Delivered",
        data: campaignPerformance.map((item) =>
          Number(item?.delivered || 0)
        ),
      },
      {
        name: "Failed",
        data: campaignPerformance.map((item) =>
          Number(item?.failed || 0)
        ),
      },
    ],
    options: {
      chart: {
        type: "bar",
        toolbar: { show: false },
      },
      xaxis: {
        categories: campaignPerformance.map(
          (item) => item?.campaign || item?.date || ""
        ),
      },
      plotOptions: {
        bar: {
          borderRadius: 5,
          columnWidth: "45%",
        },
      },
      legend: {
        position: "bottom",
      },
    },
  };

  if (loading) {
    return (
      <div className="crm-dashboard crm-dashboard-loading">
        <div className="crm-loader-ring"></div>
      </div>
    );
  }

  return (
    <div className="crm-dashboard">
      <section className="crm-dashboard-hero">
        <div className="crm-hero-glow"></div>

        <div className="crm-hero-content">
          <div>
            <span className="crm-ai-badge">
              <MdSms /> BULK SMS
            </span>

            <h1>Bulk SMS Dashboard</h1>

            <p>
              Monitor SMS campaigns, delivery performance, credits and
              messaging activity.
            </p>

            <div className="crm-hero-meta">
              <span>
                <MdTrendingUp />
                Messaging analytics
              </span>
            </div>
          </div>

          <div className="crm-hero-stats">
            <div>
              <strong>{currentData?.sms_sent || 0}</strong>
              <span>Messages Sent</span>
            </div>

            <div>
              <strong>{currentData?.delivery_rate || 0}%</strong>
              <span>Delivery Rate</span>
            </div>
          </div>
        </div>
      </section>

      <div className="crm-section-head">
        <div>
          <span className="crm-eyebrow">BULK SMS OVERVIEW</span>
          <h2>Messaging Analytics</h2>
        </div>

        <div className="crm-view-switch">
          <button
            className={view === "overall" ? "active" : ""}
            onClick={() => setView("overall")}
          >
            Overall
          </button>

          <button
            className={view === "today" ? "active" : ""}
            onClick={() => setView("today")}
          >
            Today
          </button>
        </div>
      </div>

      <div className="crm-kpi-grid">
        {tiles.map((item, index) => (
          <Link
            to={item.href}
            className={`crm-kpi-card ${item.accent}`}
            key={index}
          >
            <div className="crm-kpi-top">
              <div className="crm-kpi-icon">{item.icon}</div>
              <MdArrowForward className="crm-kpi-arrow" />
            </div>

            <div className="crm-kpi-value">{item.points}</div>
            <div className="crm-kpi-label">{item.label}</div>
            <div className="crm-kpi-helper">{item.helper}</div>
            <div className="crm-kpi-line"></div>
          </Link>
        ))}
      </div>

      <div className="crm-chart-grid">
        <div className="crm-chart-card">
          <div className="crm-card-heading">
            <div>
              <span className="crm-live-dot"></span>
              Delivery Status
            </div>
            <span className="crm-period-chip">Live</span>
          </div>

          <div className="crm-chart-body">
            <Chart
              options={deliveryChart.options}
              series={deliveryChart.series}
              type="donut"
              height={320}
            />
          </div>
        </div>

        <div className="crm-chart-card">
          <div className="crm-card-heading">
            <div>
              <span className="crm-live-dot"></span>
              Campaign Performance
            </div>
            <span className="crm-period-chip">Campaigns</span>
          </div>

          <div className="crm-chart-body">
            {campaignPerformance.length ? (
              <Chart
                options={campaignChart.options}
                series={campaignChart.series}
                type="bar"
                height={320}
              />
            ) : (
              <div className="crm-empty-chart">
                No campaign data available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkSMSDashboard;